import React, { useState, useRef } from 'react';
import { 
  FileText, 
  Calendar as CalendarIcon,
  User,
  Briefcase,
  Loader2,
  Download,
  Image as ImageIcon,
  X,
  Calendar,
  Hash
} from 'lucide-react';
import { generateExperienceLetterPDF } from '../../utils/pdfExperienceLetter';
import toast from 'react-hot-toast';

export const ExperienceLetterCreatePage: React.FC = () => {
  const [date, setDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [refNo, setRefNo] = useState<string>('');
  const [employeeName, setEmployeeName] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [letterContent, setLetterContent] = useState<string>('');
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState<string>('');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [signatureFileName, setSignatureFileName] = useState<string>('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  };

  const formatDateFromInput = (dateInput: string): string => {
    if (!dateInput) return '';
    try {
      const d = new Date(dateInput);
      const day = d.getDate();
      const month = d.toLocaleDateString('en-IN', { month: 'long' });
      const year = d.getFullYear();
      
      // Add ordinal suffix
      const getOrdinal = (n: number) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      
      return `${getOrdinal(day)} ${month} ${year}`;
    } catch {
      return dateInput;
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setLogoImage(result);
      setLogoFileName(file.name);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoImage(null);
    setLogoFileName('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setSignatureImage(result);
      setSignatureFileName(file.name);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSignature = () => {
    setSignatureImage(null);
    setSignatureFileName('');
    if (signatureInputRef.current) {
      signatureInputRef.current.value = '';
    }
  };

  const handleCreateExperienceLetter = async () => {
    // Validation
    if (!date.trim()) {
      toast.error('Please select a date');
      return;
    }
    if (!employeeName.trim()) {
      toast.error('Please enter employee name');
      return;
    }
    if (!role.trim()) {
      toast.error('Please enter role/designation');
      return;
    }
    if (!startDate.trim()) {
      toast.error('Please enter start date');
      return;
    }
    if (!endDate.trim()) {
      toast.error('Please enter end date');
      return;
    }

    setIsGenerating(true);
    
    try {
      const formattedDate = formatDateFromInput(date);
      
      await generateExperienceLetterPDF({
        date: formattedDate,
        refNo: refNo.trim() || undefined,
        employeeName: employeeName.trim(),
        role: role.trim(),
        startDate: startDate.trim(),
        endDate: endDate.trim(),
        letterContent: letterContent.trim() || undefined,
        logoUrl: logoImage || undefined,
        signatureImageUrl: signatureImage || undefined
      });
      
      toast.success('Experience Letter PDF downloaded successfully!');
      
      // Reset form (but keep date as today)
      setEmployeeName('');
      setRole('');
      setStartDate('');
      setEndDate('');
      setRefNo('');
      setLetterContent('');
      setLogoImage(null);
      setLogoFileName('');
      setSignatureImage(null);
      setSignatureFileName('');
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      if (signatureInputRef.current) {
        signatureInputRef.current.value = '';
      }
      const today = new Date();
      setDate(today.toISOString().split('T')[0]);
    } catch (error: any) {
      console.error('Error generating experience letter PDF:', error);
      toast.error(error.message || 'Failed to generate experience letter PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Create Experience Letter</h1>
              <p className="text-sm text-gray-500">Generate Experience-cum-Relieving Letter</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleCreateExperienceLetter(); }}>
              <div className="space-y-6">
                {/* Date */}
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      id="date"
                      value={date}
                      onChange={handleDateChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Reference Number */}
                <div>
                  <label htmlFor="refNo" className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number (Optional)
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="refNo"
                      value={refNo}
                      onChange={(e) => setRefNo(e.target.value)}
                      placeholder="Enter reference number"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Employee Name */}
                <div>
                  <label htmlFor="employeeName" className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="employeeName"
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                      required
                      placeholder="Enter employee name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role / Designation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      required
                      placeholder="Enter role or designation"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* End Date */}
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Letter Content */}
                <div>
                  <label htmlFor="letterContent" className="block text-sm font-medium text-gray-700 mb-2">
                    Letter Content (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Leave blank to use default content. Customize the letter content here.
                  </p>
                  <textarea
                    id="letterContent"
                    value={letterContent}
                    onChange={(e) => setLetterContent(e.target.value)}
                    rows={6}
                    placeholder="Enter custom letter content (optional)..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  />
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo (Optional)
                  </label>
                  <div className="space-y-3">
                    {logoImage ? (
                      <div className="relative border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <ImageIcon className="h-5 w-5 text-primary-600" />
                            <span className="text-sm font-medium text-gray-700">
                              {logoFileName || 'Logo Image'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="flex items-center justify-center bg-white rounded border border-gray-200 p-3">
                          <img
                            src={logoImage}
                            alt="Logo preview"
                            className="max-h-32 max-w-full object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 5MB)</p>
                        </div>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Signature Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signature Image (Optional)
                  </label>
                  <div className="space-y-3">
                    {signatureImage ? (
                      <div className="relative border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <ImageIcon className="h-5 w-5 text-primary-600" />
                            <span className="text-sm font-medium text-gray-700">
                              {signatureFileName || 'Signature Image'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveSignature}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="flex items-center justify-center bg-white rounded border border-gray-200 p-3">
                          <img
                            src={signatureImage}
                            alt="Signature preview"
                            className="max-h-32 max-w-full object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 5MB)</p>
                        </div>
                        <input
                          ref={signatureInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleSignatureUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isGenerating}
                    className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5" />
                        <span>Create Experience Letter</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

